export type ListNavigationKey =
    | "up"
    | "down"
    | "left"
    | "right"
    | "rowStart"
    | "rowEnd";

export interface ListNavigationInput {
    key: ListNavigationKey;
    rowIndex: number;
    /** `null`: the row holds focus. */
    columnId: string | null;
    columnIds: readonly string[];
    rowCount: number;
    /** The first row focus can land on, past any leading separators. */
    firstRowIndex: number;
    canExpand: boolean;
    isExpanded: boolean;
}

export type ListNavigationTarget =
    | { type: "row"; rowIndex: number }
    | { type: "cell"; rowIndex: number; columnId: string }
    | { type: "expand" }
    | { type: "stay" };

const STAY: ListNavigationTarget = { type: "stay" };

const toCell = (
    rowIndex: number,
    columnId: string | undefined,
): ListNavigationTarget =>
    columnId === undefined ? STAY : { type: "cell", rowIndex, columnId };

const fromRow = ({
    key,
    rowIndex,
    columnIds,
    canExpand,
    isExpanded,
}: ListNavigationInput): ListNavigationTarget | null => {
    if (key !== "right" || columnIds.length === 0) {
        return null;
    }
    if (canExpand && !isExpanded) {
        return { type: "expand" };
    }
    return toCell(rowIndex, columnIds[0]);
};

/**
 * Where a key moves focus in a list editing table, following the ARIA
 * treegrid pattern: Right from the row expands it or steps into its first
 * cell, Left from the first cell steps back out, and Up / Down from a cell
 * keep the column. Keys the row handles as in row mode (and Up from the
 * first focusable row, which reaches the header) return `null`.
 */
export const getListNavigationTarget = (
    input: ListNavigationInput,
): ListNavigationTarget | null => {
    const { key, rowIndex, columnId, columnIds, rowCount, firstRowIndex } =
        input;
    if (columnId === null) {
        return fromRow(input);
    }
    const columnIndex = columnIds.indexOf(columnId);
    switch (key) {
        case "left":
            return columnIndex <= 0
                ? { type: "row", rowIndex }
                : toCell(rowIndex, columnIds[columnIndex - 1]);
        case "right":
            return toCell(rowIndex, columnIds[columnIndex + 1]);
        case "up":
            return rowIndex <= firstRowIndex
                ? null
                : toCell(rowIndex - 1, columnId);
        case "down":
            return rowIndex >= rowCount - 1
                ? STAY
                : toCell(rowIndex + 1, columnId);
        case "rowStart":
            return toCell(rowIndex, columnIds[0]);
        case "rowEnd":
            return toCell(rowIndex, columnIds[columnIds.length - 1]);
    }
};
